import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { Phone, Users, CheckSquare, Clock, Mail, X, Trash2, Pencil, Check } from 'lucide-react';
import api, { organizationsAPI } from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../contexts/AuthContext';

const TYPE_META = {
  call:     { label: 'Call',     Icon: Phone },
  meeting:  { label: 'Meeting',  Icon: Users },
  todo:     { label: 'To-do',    Icon: CheckSquare },
  deadline: { label: 'Deadline', Icon: Clock },
  email:    { label: 'Email',    Icon: Mail },
};

const PRIORITY_OPTIONS = [
  { value: 'low',    label: 'Low',    color: 'bg-gray-100 text-gray-700',   dot: 'bg-gray-400'  },
  { value: 'normal', label: 'Normal', color: 'bg-blue-50 text-blue-700',     dot: 'bg-blue-400'  },
  { value: 'high',   label: 'High',   color: 'bg-red-50 text-red-700',       dot: 'bg-red-500'   },
];

const formatDueLabel = (date) => {
  if (!date) return 'No due date';
  const d = new Date(date);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Today, ${time}`;
  if (isTomorrow) return `Tomorrow, ${time}`;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Centered modal for editing a single task. Used by Tasks page (list /
// kanban / calendar) and LeadTasksWidget so the experience is identical
// no matter where the user clicked. Closes on Esc or backdrop click;
// Save / Delete callbacks come from the parent (so the parent owns the
// mutation cache invalidation).
const TaskEditModal = ({ task, isOpen, onClose, onSave, onDelete, onToggle, lockedLeadId, initialMode = 'view' }) => {
  const { user: currentUser } = useAuth();
  const canAssignAnyone = currentUser?.permissions?.view_all_leads === true || currentUser?.role === 'superadmin';

  // Existing tasks open in 'view' mode (preview); newly-created start in
  // 'edit'. The Edit button swaps view → edit; Save returns to view.
  const [mode, setMode] = useState('view');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('todo');
  const [priority, setPriority] = useState('normal');
  const [dueDate, setDueDate] = useState(null);
  const [assignedTo, setAssignedTo] = useState('');
  const [linkedLead, setLinkedLead] = useState(null);
  const [leadSearch, setLeadSearch] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);

  // Re-seed every time the modal opens so leftover state from a previous
  // edit doesn't leak into the next one.
  useEffect(() => {
    if (!isOpen) return;
    setTitle(task?.title || '');
    setDescription(task?.description || '');
    setType(task?.type || 'todo');
    setPriority(task?.priority || 'normal');
    setDueDate(task?.dueDate ? new Date(task.dueDate) : null);
    setAssignedTo(task?.assignedTo?._id || task?.assignedTo || '');
    setLinkedLead(task?.leadId || null);
    setLeadSearch('');
    setShowLeadDropdown(false);
    // Existing tasks honour the caller's `initialMode` (defaults to
    // 'view' for the Kanban / Calendar / LeadTasksWidget flows where
    // a quick preview is useful; the List view passes 'edit' so the
    // Pencil button jumps straight into the form). New tasks always
    // start in edit — there's nothing to preview yet.
    setMode(task?._id ? initialMode : 'edit');
  }, [task, isOpen, initialMode]);

  // Esc to close. Modal-only listener so we don't compete with other Esc
  // handlers on the page.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const { data: leadResults = [] } = useQuery(
    ['task-modal-lead-search', leadSearch],
    () => api.get('/leads', { params: { search: leadSearch, limit: 8 } })
      .then((r) => Array.isArray(r.data) ? r.data : (r.data?.leads || [])),
    { enabled: showLeadDropdown && leadSearch.length >= 1 && !lockedLeadId, staleTime: 30 * 1000 }
  );

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { enabled: canAssignAnyone, staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];

  if (!isOpen) return null;

  const submit = () => {
    if (!title.trim()) return toast.error('Title is required');
    const patch = {
      title: title.trim(),
      description: description.trim(),
      type,
      priority,
      dueDate: dueDate ? dueDate.toISOString() : null,
    };
    if (!lockedLeadId) {
      patch.leadId = linkedLead?._id || null;
    }
    if (canAssignAnyone) patch.assignedTo = assignedTo || null;
    onSave(task, patch);
    // After save, swap back to view so the modal stays open with the
    // updated values rather than vanishing — feels more like Pipedrive.
    if (task?._id) setMode('view');
  };

  const handleDeleteClick = () => {
    if (!onDelete) return;
    if (window.confirm('Delete this task? This can\'t be undone.')) {
      onDelete(task);
    }
  };

  // VIEW MODE — read-only preview with action buttons.
  if (mode === 'view') {
    const TypeIcon = (TYPE_META[task?.type || 'todo'] || TYPE_META.todo).Icon;
    const typeLabel = (TYPE_META[task?.type || 'todo'] || TYPE_META.todo).label;
    const priorityMeta = PRIORITY_OPTIONS.find((p) => p.value === (task?.priority || 'normal'));
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
              <TypeIcon size={14} /> {typeLabel}
              {task?.priority && task.priority !== 'normal' && priorityMeta && (
                <span className={`inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${priorityMeta.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${priorityMeta.dot}`} />
                  {priorityMeta.label}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 p-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3">
              {onToggle && (
                <button
                  type="button"
                  onClick={() => { onToggle(task); onClose(); }}
                  title={task.completed ? 'Mark not done' : 'Mark done'}
                  className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0
                    ${task.completed
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'bg-white border-gray-300 hover:border-green-500 hover:bg-green-50 text-transparent hover:text-green-500'}`}
                >
                  <Check size={14} strokeWidth={3} />
                </button>
              )}
              <h2 className={`text-xl font-bold flex-1 ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                {task.title}
              </h2>
            </div>

            {task.description && (
              <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-md p-3 border border-gray-100">
                {task.description}
              </div>
            )}

            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Due</div>
                <div className="text-gray-900">{formatDueLabel(task.dueDate)}</div>
              </div>
              {task.assignedTo && (
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Assignee</div>
                  <div className="text-gray-900">{task.assignedTo.name || task.assignedTo.email || '—'}</div>
                </div>
              )}
              {task.leadId && (
                <div className="col-span-2">
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Linked lead</div>
                  <Link
                    to={`/leads/${task.leadId._id}`}
                    onClick={onClose}
                    className="text-primary-600 hover:underline"
                  >
                    {task.leadId.firstName} {task.leadId.lastName}
                    {task.leadId.company && <span className="text-gray-500 text-xs"> — {task.leadId.company}</span>}
                  </Link>
                </div>
              )}
            </div>

            {(task.createdBy || task.completedBy) && (
              <div className="text-[11px] text-gray-500 border-t border-gray-100 pt-3 space-y-0.5">
                {task.createdBy && (
                  <div>Created by <span className="text-gray-700">{task.createdBy.name || task.createdBy.email}</span>
                    {task.createdAt && ` · ${new Date(task.createdAt).toLocaleString()}`}
                  </div>
                )}
                {task.completed && task.completedBy && (
                  <div>Completed by <span className="text-gray-700">{task.completedBy.name || task.completedBy.email}</span>
                    {task.completedAt && ` · ${new Date(task.completedAt).toLocaleString()}`}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-200 bg-gray-50">
            {onDelete ? (
              <button
                onClick={handleDeleteClick}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-md font-medium"
              >
                <Trash2 size={14} /> Delete
              </button>
            ) : <div />}
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-md"
              >
                Close
              </button>
              <button
                onClick={() => setMode('edit')}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium"
              >
                <Pencil size={14} /> Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // EDIT MODE — full form below.
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {task?._id ? 'Edit task' : 'New task'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Title</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to get done?"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notes, context, instructions…"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
              >
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Due date</label>
            <DatePicker
              selected={dueDate}
              onChange={(d) => setDueDate(d)}
              showTimeSelect
              timeIntervals={15}
              dateFormat="MMM d, yyyy h:mm aa"
              placeholderText="No due date"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
              wrapperClassName="w-full"
              isClearable
            />
          </div>

          {!lockedLeadId && (
            <div className="relative">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Linked lead</label>
              {linkedLead ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-md text-sm">
                  <span className="font-medium text-primary-900">
                    {linkedLead.firstName} {linkedLead.lastName}
                  </span>
                  {linkedLead.company && (
                    <span className="text-xs text-gray-500">— {linkedLead.company}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => { setLinkedLead(null); setLeadSearch(''); }}
                    className="ml-auto text-gray-400 hover:text-red-600"
                    title="Unlink"
                  >×</button>
                </div>
              ) : (
                <>
                  <input
                    value={leadSearch}
                    onChange={(e) => { setLeadSearch(e.target.value); setShowLeadDropdown(true); }}
                    onFocus={() => setShowLeadDropdown(true)}
                    onBlur={() => setTimeout(() => setShowLeadDropdown(false), 150)}
                    placeholder="Search a lead to link (optional)…"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                  />
                  {showLeadDropdown && leadResults.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {leadResults.slice(0, 8).map((l) => (
                        <button
                          key={l._id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setLinkedLead(l); setShowLeadDropdown(false); setLeadSearch(''); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{l.firstName} {l.lastName}</div>
                          {l.company && <div className="text-xs text-gray-500">{l.company}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {canAssignAnyone && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Assigned to</label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white"
              >
                <option value="">Unassigned</option>
                {orgUsers.map((u) => (
                  <option key={u._id} value={u._id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
          )}

          {task?._id && (task?.createdBy || task?.completedBy) && (
            <div className="text-[11px] text-gray-500 border-t border-gray-100 pt-3 space-y-0.5">
              {task.createdBy && (
                <div>Created by <span className="text-gray-700">{task.createdBy.name || task.createdBy.email}</span>
                  {task.createdAt && ` · ${new Date(task.createdAt).toLocaleString()}`}
                </div>
              )}
              {task.completed && task.completedBy && (
                <div>Completed by <span className="text-gray-700">{task.completedBy.name || task.completedBy.email}</span>
                  {task.completedAt && ` · ${new Date(task.completedAt).toLocaleString()}`}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-200 bg-gray-50">
          {task?._id && onDelete ? (
            <button
              onClick={handleDeleteClick}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-md font-medium"
            >
              <Trash2 size={14} /> Delete
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium"
            >
              {task?._id ? 'Save changes' : 'Create task'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskEditModal;
